import type { OrderRow } from '../../api/amazonconnect';
import { inWindow, type DayWindow } from './dayWindows';
import { parseOrderTotal } from './format';

export type WindowAggregate = {
  orderCount: number;
  /** Orders with parseable OrderTotal. */
  ordersWithTotal: number;
  sales: number | null;
  currencies: string[];
  /** Sum of header quantity when quantity > 0. */
  units: number | null;
  ordersWithUnits: number;
  unshipped: number;
  canceled: number;
  aov: number | null;
};

const OPEN_STATUSES = new Set([
  'unshipped',
  'partiallyshipped',
  'pending',
  'pendingavailability',
]);

function statusKey(s?: string): string {
  return (s ?? '').replace(/\s+/g, '').toLowerCase();
}

export function filterOrders(orders: OrderRow[], win: DayWindow): OrderRow[] {
  return orders.filter((o) => inWindow(o.purchase_date, win));
}

export function aggregateOrders(orders: OrderRow[]): WindowAggregate {
  let orderCount = 0;
  let ordersWithTotal = 0;
  let salesSum = 0;
  const ccy = new Set<string>();
  let unitsSum = 0;
  let ordersWithUnits = 0;
  let unshipped = 0;
  let canceled = 0;

  for (const o of orders) {
    orderCount += 1;
    const sk = statusKey(o.order_status);
    if (OPEN_STATUSES.has(sk)) unshipped += 1;
    if (sk === 'canceled' || sk === 'cancelled') canceled += 1;

    const total = parseOrderTotal(o.order_total);
    if (total != null) {
      ordersWithTotal += 1;
      salesSum += total;
      if (o.currency) ccy.add(o.currency.toUpperCase());
    }

    if (typeof o.quantity === 'number' && o.quantity > 0) {
      ordersWithUnits += 1;
      unitsSum += o.quantity;
    }
  }

  const multiCcy = ccy.size > 1;
  // Honest zeros when the window has no orders; null when totals/units missing on existing rows.
  const sales: number | null = multiCcy
    ? null
    : ordersWithTotal === 0
      ? orderCount === 0
        ? 0
        : null
      : salesSum;

  const units: number | null =
    ordersWithUnits === 0 ? (orderCount === 0 ? 0 : null) : unitsSum;
  const aov: number | null =
    ordersWithTotal > 0 && !multiCcy ? salesSum / ordersWithTotal : null;

  return {
    orderCount,
    ordersWithTotal,
    sales,
    currencies: [...ccy],
    units,
    ordersWithUnits,
    unshipped,
    canceled,
    aov,
  };
}

export function windowAggregate(orders: OrderRow[], win: DayWindow): WindowAggregate {
  return aggregateOrders(filterOrders(orders, win));
}
